/**
 * @fileoverview Utilitário responsável por concentrar helpers e estruturas de otimização de performance.
 */

/**
 * Utilitários de performance para otimização do chat
 */

/**
 * Throttle: Limita a execução de uma função a uma vez a cada X ms
 * Útil para scroll handlers, resize, etc.
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

/**
 * Debounce: Atrasa a execução de uma função até que ela pare de ser chamada por X ms
 * Útil para search inputs, auto-save, etc.
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * RequestIdleCallback polyfill
 * Executa código quando o navegador está ocioso
 */
export const requestIdleCallback =
  window.requestIdleCallback ||
  function(cb) {
    const start = Date.now();
    return setTimeout(() => {
      cb({
        didTimeout: false,
        timeRemaining: () => Math.max(0, 50 - (Date.now() - start))
      });
    }, 1);
  };

/**
 * CancelIdleCallback polyfill
 */
export const cancelIdleCallback =
  window.cancelIdleCallback ||
  function(id) {
    clearTimeout(id);
  };

/**
 * Lazy load de imagens com Intersection Observer
 * Melhora o carregamento inicial da página
 */
export const lazyLoadImage = (img) => {
  if ('IntersectionObserver' in window) {
    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const image = entry.target;
          const src = image.dataset.src;

          if (src) {
            image.src = src;
            image.classList.add('loaded');
          }

          observer.unobserve(image);
        }
      });
    }, {
      rootMargin: '50px 0px', // Começa a carregar 50px antes de ficar visível
      threshold: 0.01
    });

    imageObserver.observe(img);
  } else {
    // Fallback para navegadores sem suporte a IntersectionObserver
    img.src = img.dataset.src;
  }
};

/**
 * Memoização simples para funções puras
 * Cache de resultados de funções computacionalmente custosas
 */
export const memoize = (fn) => {
  const cache = new Map();
  return (...args) => {
    const key = JSON.stringify(args);
    if (cache.has(key)) {
      return cache.get(key);
    }
    const result = fn(...args);
    cache.set(key, result);
    return result;
  };
};

/**
 * Cache com expiração para dados temporários
 */
class TimedCache {
  constructor(ttl = 5 * 60 * 1000) { // 5 minutos por padrão
    this.cache = new Map();
    this.ttl = ttl;
  }

  set(key, value) {
    const expiresAt = Date.now() + this.ttl;
    this.cache.set(key, { value, expiresAt });
  }

  get(key) {
    const cached = this.cache.get(key);
    if (!cached) return null;

    if (Date.now() > cached.expiresAt) {
      this.cache.delete(key);
      return null;
    }

    return cached.value;
  }

  clear() {
    this.cache.clear();
  }

  has(key) {
    const cached = this.cache.get(key);
    if (!cached) return false;

    if (Date.now() > cached.expiresAt) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }
}

export const messageCache = new TimedCache(10 * 60 * 1000); // 10 minutos

/**
 * Batch de atualizações para evitar re-renders múltiplos
 */
export class BatchUpdater {
  constructor(callback, delay = 100) {
    this.callback = callback;
    this.delay = delay;
    this.updates = [];
    this.timeoutId = null;
  }

  add(update) {
    this.updates.push(update);

    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    this.timeoutId = setTimeout(() => {
      this.flush();
    }, this.delay);
  }

  flush() {
    if (this.updates.length > 0) {
      this.callback(this.updates);
      this.updates = [];
    }
    this.timeoutId = null;
  }

  clear() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    this.updates = [];
  }
}

/**
 * Detecta se o usuário está em uma conexão lenta
 */
export const isSlowConnection = () => {
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

  if (!connection) return false;

  // Considera lento se:
  // - saveData está ativo (economia de dados)
  // - effectiveType é slow-2g ou 2g
  // - downlink é menor que 1 Mbps
  return (
    connection.saveData ||
    connection.effectiveType === 'slow-2g' ||
    connection.effectiveType === '2g' ||
    (connection.downlink && connection.downlink < 1)
  );
};

/**
 * Obtém informações de performance da página
 */
export const getPerformanceMetrics = () => {
  if (!window.performance) return null;

  const navigation = performance.getEntriesByType('navigation')[0];
  const paint = performance.getEntriesByType('paint');

  return {
    // Tempo de carregamento da página
    pageLoad: navigation ? navigation.loadEventEnd - navigation.fetchStart : 0,

    // Tempo até o DOM estar interativo
    domInteractive: navigation ? navigation.domInteractive - navigation.fetchStart : 0,

    // First Paint (FP)
    firstPaint: paint.find(entry => entry.name === 'first-paint')?.startTime || 0,

    // First Contentful Paint (FCP)
    firstContentfulPaint: paint.find(entry => entry.name === 'first-contentful-paint')?.startTime || 0,

    // Memória usada (se disponível)
    memory: performance.memory ? {
      usedJSHeapSize: performance.memory.usedJSHeapSize,
      totalJSHeapSize: performance.memory.totalJSHeapSize,
      limit: performance.memory.jsHeapSizeLimit
    } : null
  };
};

/**
 * Log de performance apenas em desenvolvimento
 */
export const logPerformance = (label, startTime) => {
  if (process.env.NODE_ENV === 'development') {
    const endTime = performance.now();
    const duration = endTime - startTime;
    console.log(`⚡ [Performance] ${label}: ${duration.toFixed(2)}ms`);
  }
};

/**
 * Marca início de medição de performance
 */
export const startPerformanceMark = (label) => {
  if (process.env.NODE_ENV === 'development' && window.performance) {
    performance.mark(`${label}-start`);
  }
};

/**
 * Marca fim de medição e calcula duração
 */
export const endPerformanceMark = (label) => {
  if (process.env.NODE_ENV === 'development' && window.performance) {
    performance.mark(`${label}-end`);
    performance.measure(label, `${label}-start`, `${label}-end`);

    const measure = performance.getEntriesByName(label)[0];
    console.log(`⚡ [Performance] ${label}: ${measure.duration.toFixed(2)}ms`);

    // Limpa as marcas
    performance.clearMarks(`${label}-start`);
    performance.clearMarks(`${label}-end`);
    performance.clearMeasures(label);
  }
};

export default {
  throttle,
  debounce,
  requestIdleCallback,
  cancelIdleCallback,
  lazyLoadImage,
  memoize,
  messageCache,
  BatchUpdater,
  isSlowConnection,
  getPerformanceMetrics,
  logPerformance,
  startPerformanceMark,
  endPerformanceMark
};
